import { CalendarMonth } from '@project-lary/react-material-symbols';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../utils/tailwind-merge';

interface DateInputProps {
  value?: Date;
  className?: string;
  hasError?: boolean;
  onChange: (date: Date) => void;
}

interface DateParts {
  day: string;
  month: string;
  year: string;
}

const formatTwoDigits = (value: number) => value.toString().padStart(2, '0');

const formatDateStr = (value: DateParts) => ({
  day: formatTwoDigits(Number.parseInt(value.day, 10)),
  month: formatTwoDigits(Number.parseInt(value.month, 10)),
  year: value.year,
});

const DateInput: React.FC<DateInputProps> = ({ value, hasError, className, onChange }) => {
  const monthRef = useRef<HTMLInputElement | null>(null);
  const dayRef = useRef<HTMLInputElement | null>(null);
  const yearRef = useRef<HTMLInputElement | null>(null);

  const [date, setDate] = useState<DateParts>(() => {
    if (value) {
      const d = new Date(value);
      return {
        day: formatTwoDigits(d.getDate()),
        month: formatTwoDigits(d.getMonth() + 1), // Adjust for 0-indexed months
        year: d.getFullYear().toString(),
      };
    }
    return { day: '', month: '', year: '' }; // Use empty strings for placeholders
  });

  useEffect(() => {
    if (value) {
      const d = new Date(value);
      setDate({
        day: formatTwoDigits(d.getDate()),
        month: formatTwoDigits(d.getMonth() + 1), // Adjust for 0-indexed months
        year: d.getFullYear().toString(),
      });
    } else {
      setDate({ day: '', month: '', year: '' }); // Reset to placeholders if value is undefined
    }
  }, [value]);

  const validateDate = (field: keyof DateParts, value: number): boolean => {
    if (
      (field === 'day' && (value < 1 || value > 31)) ||
      (field === 'month' && (value < 1 || value > 12)) ||
      (field === 'year' && (value < 1000 || value > 9999))
    ) {
      return false;
    }

    // Validate the day of the month
    const newDate = { ...date, [field]: value };
    const year = Number.parseInt(newDate.year, 10);
    const month = Number.parseInt(newDate.month, 10);
    const day = Number.parseInt(newDate.day, 10);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return false;

    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() + 1 === month && d.getDate() === day;
  };

  const handleInputChange = (field: keyof DateParts) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value ? e.target.value : '';

    setDate({ ...date, [field]: newValue });

    if ((field === 'day' || field === 'month') && newValue.length === 2) {
      field === 'day' ? monthRef.current?.focus() : yearRef.current?.focus();
    }
  };

  const initialDate = useRef<DateParts>(date);

  const handleBlur =
    (field: keyof DateParts) =>
    (e: React.FocusEvent<HTMLInputElement>): void => {
      if (!e.target.value) {
        setDate(initialDate.current);
        return;
      }

      const newValue = Number.parseInt(e.target.value, 10);
      const isValid = validateDate(field, newValue);

      if (isValid) {
        // If the new value is valid, update the initial value
        initialDate.current = formatDateStr({ ...date, [field]: newValue });
      }

      setDate(initialDate.current);
      // only call onChange when the entry is valid
      if (isValid) {
        onChange(
          new Date(Number.parseInt(date.year, 10), Number.parseInt(date.month, 10) - 1, Number.parseInt(date.day, 10)),
        );
      }
    };

  const handleKeyDown = (field: keyof DateParts) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow command (or control) combinations
    if (e.metaKey || e.ctrlKey) {
      return;
    }

    // Prevent non-numeric characters, excluding allowed keys
    if (
      !/^[0-9]$/.test(e.key) &&
      !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Backspace', 'Enter'].includes(e.key)
    ) {
      e.preventDefault();
      return;
    }

    // Convert current state to numbers for manipulation
    const day = Number.parseInt(date.day, 10) || 1;
    const month = Number.parseInt(date.month, 10) || 1;
    const year = Number.parseInt(date.year, 10) || new Date().getFullYear();

    const newDate: DateParts = { ...date };

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (field === 'day') {
        const newDay = day === new Date(year, month, 0).getDate() ? 1 : day + 1;
        newDate.day = formatTwoDigits(newDay);
      } else if (field === 'month') {
        const newMonth = month === 12 ? 1 : month + 1;
        newDate.month = formatTwoDigits(newMonth);
      } else if (field === 'year') {
        newDate.year = (year + 1).toString();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (field === 'day') {
        const newDay = day === 1 ? new Date(year, month, 0).getDate() : day - 1;
        newDate.day = formatTwoDigits(newDay);
      } else if (field === 'month') {
        const newMonth = month === 1 ? 12 : month - 1;
        newDate.month = formatTwoDigits(newMonth);
      } else if (field === 'year') {
        newDate.year = (year - 1).toString();
      }
    }

    if (e.key === 'ArrowRight') {
      if (
        e.currentTarget.selectionStart === e.currentTarget.value.length ||
        (e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === e.currentTarget.value.length)
      ) {
        e.preventDefault();
        if (field === 'day') monthRef.current?.focus();
        if (field === 'month') yearRef.current?.focus();
      }
    } else if (e.key === 'ArrowLeft') {
      if (
        e.currentTarget.selectionStart === 0 ||
        (e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === e.currentTarget.value.length)
      ) {
        e.preventDefault();
        if (field === 'month') dayRef.current?.focus();
        if (field === 'year') monthRef.current?.focus();
      }
    }

    setDate(newDate);

    // Only call onChange if the new date is fully valid and not empty
    if (newDate.day && newDate.month && newDate.year) {
      onChange(
        new Date(
          Number.parseInt(newDate.year, 10),
          Number.parseInt(newDate.month, 10) - 1,
          Number.parseInt(newDate.day, 10),
        ),
      );
    }
  };

  return (
    <div
      className={cn(
        'flex items-center rounded-md border border-neutral-alpha-5 bg-white p-2 px-1 text-black text-sm',
        {
          'focus-within:shadow-focus-ring': !hasError,
          'border-error-8': hasError,
        },
        className,
      )}
    >
      <input
        type="text"
        ref={dayRef}
        max={31}
        maxLength={2}
        value={date.day}
        onChange={handleInputChange('day')}
        onKeyDown={handleKeyDown('day')}
        onFocus={(e) => {
          if (window.innerWidth > 1024) {
            e.target.select();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onBlur={handleBlur('day')}
        className="mx-1 w-6 rounded-md border-none text-center hover:bg-neutral-alpha-3 focus:bg-neutral-alpha-3 focus:outline-none"
        placeholder="TT"
      />
      <span className="opacity-20">/</span>
      <input
        type="text"
        ref={monthRef}
        max={12}
        maxLength={2}
        onClick={(e) => e.stopPropagation()}
        value={date.month}
        onChange={handleInputChange('month')}
        onKeyDown={handleKeyDown('month')}
        onFocus={(e) => {
          if (window.innerWidth > 1024) {
            e.target.select();
          }
        }}
        onBlur={handleBlur('month')}
        className="mx-1 w-6 rounded-md border-none text-center hover:bg-neutral-alpha-3 focus:bg-neutral-alpha-3 focus:outline-none"
        placeholder="MM"
      />
      <span className="opacity-20">/</span>
      <input
        type="text"
        ref={yearRef}
        max={9999}
        maxLength={4}
        onClick={(e) => e.stopPropagation()}
        value={date.year}
        onChange={handleInputChange('year')}
        onKeyDown={handleKeyDown('year')}
        onFocus={(e) => {
          if (window.innerWidth > 1024) {
            e.target.select();
          }
        }}
        onBlur={handleBlur('year')}
        className="w-12 rounded-md border-none p-0 text-center hover:bg-neutral-alpha-3 focus:bg-neutral-alpha-3 focus:outline-none"
        placeholder="JJJJ"
      />
      <CalendarMonth className="mr-1 ml-auto size-6 text-neutral-11 hover:text-neutral-12" />
    </div>
  );
};

DateInput.displayName = 'DateInput';

export { DateInput };
